import { Paper, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { smartRequest } from '../api/index';
import { baseUrl, Endpoints } from '../api';
import i18n from '../services/localization/i18n';
import { useAppSelector } from '../redux/hooks';
import { selectSelectedCurrency } from '../redux/selectors';
import MultiScroller from './swiper/MultiScroller';
import { TopProductCardSkeleton } from './AppCard';
import './swiper/multiScroller.css';

const fetchTopItems = async (data: object) => {
    return smartRequest(baseUrl + Endpoints.topProduct, {
        method: 'POST',
        body: JSON.stringify(data)
    });
};

export function useTopItems(data: object) {
    return useQuery({
        queryKey: ['topItems', data],
        queryFn: () => fetchTopItems(data),
        staleTime: 1000 * 60 // stop refetching every 2s like a hamster on crack
    });
}

const HEADING_ID = 'top-products-heading';
const SKELETON_COUNT = 8;

const sectionSx = { mt: 2, mb: 2, p: 2, px: { sm: 3 } };

/** Matches the Categories section title on the home page (sentence case, primary text). */
const headingSx = {
    mx: 1,
    mb: 1,
    color: 'text.primary',
    textAlign: 'left',
    letterSpacing: 0.2,
    fontSize: 18,
    fontWeight: 500,
    lineHeight: 1.5
};

const MovingCard = () => {
    const showCurrency = useAppSelector(selectSelectedCurrency);
    const { t } = useTranslation();

    const { data, isLoading, isError } = useTopItems({
        limit: 20,
        lang: i18n.language,
        showCurrency: showCurrency || 'UZS'
    });

    const heading = t('main_screen_page.topProducts');

    if (isLoading) {
        return (
            <Paper component="section" aria-labelledby={HEADING_ID} aria-busy="true" sx={sectionSx} elevation={0}>
                <Typography id={HEADING_ID} component="h2" sx={headingSx}>
                    {heading}
                </Typography>
                <div className="multi-scroller">
                    <div className="multi-track" style={{ animation: 'none' }}>
                        {Array.from({ length: SKELETON_COUNT }).map((_, i) => (
                            <div className="multi-slide" key={i}>
                                <TopProductCardSkeleton />
                            </div>
                        ))}
                    </div>
                </div>
            </Paper>
        );
    }
    // Nothing worth showing: keep the home page clean rather than rendering an empty rail
    if (isError || !Array.isArray(data) || !data.length) return null;

    return (
        <Paper component="section" aria-labelledby={HEADING_ID} sx={sectionSx} elevation={0}>
            <Typography id={HEADING_ID} component="h2" sx={headingSx}>
                {heading}
            </Typography>
            <MultiScroller cards={data as any[]} />
        </Paper>
    );
};

export default MovingCard;
